import '../styles/base.css';
import './viewer.css';
import { render } from 'preact';
import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import type { Album, AlbumManifest, SessionInfo } from '@fdpf/shared';
import { DEFAULT_SLIDE_SECONDS } from '@fdpf/shared';
import { api, ApiError } from '../lib/api.js';
import { fullscreenSupported, inStandalone, isFullscreen, isIos, keepScreenAwake, toggleFullscreen } from '../lib/kiosk.js';
import {
  albumCacheStatus,
  cacheStats,
  clearAllCaches,
  onPrecacheEvents,
  registerServiceWorker,
  serviceWorkerSupported,
  startPrecache,
  type CacheStats,
} from '../lib/precache.js';
import { getSession, isUnauthenticated, startHeartbeat } from '../lib/session.js';
import { installViewportUnit } from '../lib/viewport.js';
import { clearBootFallback } from '../lib/boot.js';
import { Icon } from '../lib/Icon.jsx';
import { currentTheme, installTheme, toggleTheme, type Theme } from '../lib/theme.js';
import { mediaUrl, Slideshow } from './slideshow.js';
import { AlbumList } from './AlbumList.jsx';

clearBootFallback();
installViewportUnit();
installTheme();

const INTERVAL_STORAGE_KEY = 'fdpf.slideSeconds';
const MUTED_STORAGE_KEY = 'fdpf.muted';
const HOME_HINT_DISMISSED_KEY = 'fdpf.homeHintDismissed';
const CHROME_HIDE_DELAY_MS = 3000;
const INTERVAL_CHOICES = [3, 5, 8, 12, 20, 30, 60];

const mb = (bytes: number) => (bytes / 1024 / 1024).toFixed(bytes < 10 * 1024 * 1024 ? 1 : 0) + ' MB';

function readStoredSeconds(): number {
  const raw = Number(localStorage.getItem(INTERVAL_STORAGE_KEY));
  return INTERVAL_CHOICES.includes(raw) ? raw : DEFAULT_SLIDE_SECONDS;
}

/** Muted by default: an unattended frame should not start making noise on its own. */
function readStoredMuted(): boolean {
  return localStorage.getItem(MUTED_STORAGE_KEY) !== '0';
}

// --- routing ----------------------------------------------------------------

function navigate(path: string): void {
  history.pushState(null, '', path);
  window.dispatchEvent(new Event('fdpf:navigate'));
}

function useRoute(): string {
  const [path, setPath] = useState(window.location.pathname);
  useEffect(() => {
    const update = () => setPath(window.location.pathname);
    window.addEventListener('popstate', update);
    window.addEventListener('fdpf:navigate', update);
    return () => {
      window.removeEventListener('popstate', update);
      window.removeEventListener('fdpf:navigate', update);
    };
  }, []);
  return path;
}

// --- album grid -------------------------------------------------------------

function HomeScreenHint() {
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(HOME_HINT_DISMISSED_KEY) === '1');
  if (dismissed || inStandalone()) return null;

  return (
    <div class="banner">
      <h2>
        <Icon name="sparkle" size={18} />
        把它装成全屏相框
      </h2>
      {isIos() ? (
        <>
          <p style={{ margin: 0 }}>
            在浏览器里看会有地址栏挡住画面，而且 iPhone 版 Safari 没有全屏功能。加到主屏幕就能全屏。
          </p>
          <ol>
            <li>点「分享」按钮</li>
            <li>往下滑，选「添加到主屏幕」</li>
            <li>从主屏幕的图标打开</li>
            <li>设置 → 显示与亮度 → 自动锁定 → 永不（需插电）</li>
          </ol>
        </>
      ) : (
        <p style={{ margin: '0 0 0.75rem' }}>
          在浏览器菜单里选「安装应用」，之后从图标打开即可全屏，并会自动阻止屏幕休眠。
        </p>
      )}
      <button
        onClick={() => {
          localStorage.setItem(HOME_HINT_DISMISSED_KEY, '1');
          setDismissed(true);
        }}
      >
        知道了，不用再提示
      </button>
    </div>
  );
}

/** 基本白 / 基本黑, the Switch's own two system themes. The stage stays black either way. */
function ThemeToggle() {
  const [theme, setThemeState] = useState<Theme>(currentTheme);
  const dark = theme === 'dark';
  return (
    <button
      class="icon-only round"
      onClick={() => setThemeState(toggleTheme())}
      aria-label={dark ? '切换到浅色' : '切换到深色'}
      title={dark ? '切换到浅色（基本白）' : '切换到深色（基本黑）'}
    >
      <Icon name={dark ? 'sun' : 'moon'} size={20} />
    </button>
  );
}

function AlbumGrid({ session }: { session: SessionInfo }) {
  const [albums, setAlbums] = useState<Album[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<{ albums: (Album & { coverHash?: string | null })[] }>('/api/albums')
      .then((res) => setAlbums(res.albums))
      .catch((err) => setError(err instanceof ApiError ? err.message : String(err)));
  }, []);

  if (error) {
    return (
      <div class="center-note">
        <span class="note-icon">
          <Icon name="alert" size={40} />
        </span>
        <h1>加载失败</h1>
        <p>{error}</p>
      </div>
    );
  }

  if (albums === null) {
    return (
      <div class="center-note">
        <p>
          <span class="spinner" />
          正在加载相簿…
        </p>
      </div>
    );
  }

  if (albums.length === 0) {
    const isAdmin = session.user.role === 'admin';
    return (
      <div class="center-note">
        <span class="note-icon">
          <Icon name="album" size={44} />
        </span>
        <h1>还没有可看的相簿</h1>
        <p>
          {isAdmin
            ? '这里还是空的。到管理后台新建一个相簿并上传照片，就能开始看了。'
            : '管理员还没有把任何相簿分享给你，可以问问管理员。'}
        </p>
        {/* An admin staring at an empty viewer previously had no way to reach the admin UI at all —
            the text told them to go there but gave them no link. */}
        {isAdmin && (
          <p>
            <a class="button-link btn-icon-label" href="/admin">
              打开管理后台
              <Icon name="chevronRight" size={18} />
            </a>
          </p>
        )}
      </div>
    );
  }

  return (
    <div class="grid-page">
      <div class="grid-head">
        <h1>家庭相框</h1>
        <div class="head-actions">
          {/* Identity and navigation are separate controls on purpose — see .user-chip in viewer.css. */}
          <span class="user-chip">
            <span class="avatar">
              <Icon name="user" size={15} />
            </span>
            {session.user.name}
          </span>
          {session.user.role === 'admin' && (
            <a class="nav-btn" href="/admin">
              <Icon name="settings" size={16} />
              管理后台
            </a>
          )}
          <ThemeToggle />
        </div>
      </div>
      <HomeScreenHint />
      <div class="albums">
        {albums.map((album) => {
          const coverHash = (album as Album & { coverHash?: string | null }).coverHash;
          return (
            <div class="album-card-wrap ring-target" key={album.id}>
              <button class="album-card" onClick={() => navigate(`/a/${album.slug}`)}>
                {coverHash ? (
                  <img class="cover" src={mediaUrl(coverHash, 'thumb')} alt="" loading="lazy" />
                ) : (
                  <span class="cover is-empty">
                    <Icon name="album" size={34} />
                  </span>
                )}
                <span class="meta">
                  <span class="title">{album.title}</span>
                  <span class="sub">
                    {album.itemCount ?? 0} 项 · {mb(album.bytesTotal ?? 0)}
                  </span>
                </span>
              </button>
              {/* A separate entry point so saving a single photo never requires sitting through the
                  slideshow. A real link, not a nested button — nesting one inside the card button
                  would be invalid HTML and swallow the click. */}
              <a
                class="album-list-link"
                href={`/a/${encodeURIComponent(album.slug)}/list`}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(`/a/${encodeURIComponent(album.slug)}/list`);
                }}
              >
                <Icon name="list" size={15} />
                文件列表（可单张下载）
              </a>
            </div>
          );
        })}
      </div>
      <CacheFooter />
    </div>
  );
}

function CacheFooter() {
  const [stats, setStats] = useState<CacheStats | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(() => {
    if (!serviceWorkerSupported()) return;
    cacheStats().then(setStats, () => setStats(null));
  }, []);

  useEffect(refresh, [refresh]);

  if (!serviceWorkerSupported()) {
    return (
      <div class="cache-footer warn">
        <Icon name="alert" size={16} className="i-mid" /> 这个浏览器不支持离线缓存，循环播放会一直消耗流量。
        {isIos() ? '请改用 Safari 打开（iOS 上的第三方浏览器做不到）。' : ''}
      </div>
    );
  }

  if (!stats) return null;

  const pct = stats.budgetBytes > 0 ? Math.min(100, Math.round((stats.totalBytes / stats.budgetBytes) * 100)) : 0;

  return (
    <div class="cache-footer">
      本机已缓存 {mb(stats.totalBytes)} / {mb(stats.budgetBytes)}（{stats.entryCount} 个文件）
      <div class="cache-bar">
        <span style={{ width: pct + '%' }} />
      </div>
      看得最久没碰的相簿会在空间不足时自动清理。{' '}
      <button
        class="small"
        disabled={busy}
        onClick={() => {
          setBusy(true);
          clearAllCaches()
            .then(refresh)
            .finally(() => setBusy(false));
        }}
      >
        {busy ? '清理中…' : '清空缓存'}
      </button>
    </div>
  );
}

// --- slideshow --------------------------------------------------------------

type CacheState =
  | { phase: 'unknown' }
  | { phase: 'unsupported' }
  | { phase: 'complete' }
  | { phase: 'caching'; done: number; total: number }
  | { phase: 'partial'; done: number; total: number; reason: 'quota' | 'failed' };

function CachePill({ state, visible }: { state: CacheState; visible: boolean }) {
  if (state.phase === 'unknown') return null;

  let text: string;
  let tone = '';
  let glyph: 'download' | 'check' | 'alert' = 'download';
  if (state.phase === 'unsupported') {
    text = '无法离线缓存，播放会持续用流量';
    tone = ' warn';
    glyph = 'alert';
  } else if (state.phase === 'caching') {
    text = `正在缓存 ${state.done}/${state.total}`;
  } else if (state.phase === 'partial') {
    text =
      state.reason === 'quota'
        ? `空间不足，只缓存了 ${state.done}/${state.total}`
        : `有 ${state.total - state.done} 项没缓存成功`;
    tone = ' warn';
    glyph = 'alert';
  } else {
    text = '已全部离线缓存';
    tone = ' ok';
    glyph = 'check';
  }

  // A finished pill fades with the rest of the chrome; an in-progress one stays put so you can watch
  // it complete on a slow link.
  const hideWithChrome = state.phase === 'complete';
  return (
    <div class={'cache-pill' + tone + (hideWithChrome && !visible ? ' chrome-hidden' : '')}>
      <Icon name={glyph} size={15} />
      {text}
    </div>
  );
}

function SlideshowView({ slug }: { slug: string }) {
  const stageRef = useRef<HTMLDivElement | null>(null);
  const slideshowRef = useRef<Slideshow | null>(null);

  const [manifest, setManifest] = useState<AlbumManifest | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [paused, setPaused] = useState(false);
  const [position, setPosition] = useState(0);
  const [seconds, setSeconds] = useState(readStoredSeconds);
  const [muted, setMuted] = useState(readStoredMuted);
  const [chromeVisible, setChromeVisible] = useState(true);
  const [notice, setNotice] = useState<string | null>(null);
  const [fullscreen, setFullscreen] = useState(isFullscreen);
  const [cacheState, setCacheState] = useState<CacheState>({ phase: 'unknown' });

  const hideTimer = useRef<number | null>(null);

  const revealChrome = useCallback(() => {
    setChromeVisible(true);
    if (hideTimer.current !== null) clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setChromeVisible(false), CHROME_HIDE_DELAY_MS) as unknown as number;
  }, []);

  useEffect(() => {
    api
      .get<AlbumManifest>(`/api/albums/${encodeURIComponent(slug)}`)
      .then(setManifest)
      .catch((err) => setError(err instanceof ApiError ? err.message : String(err)));
  }, [slug]);

  // Build the slideshow once the manifest arrives.
  useEffect(() => {
    if (!manifest || !stageRef.current) return;
    if (manifest.items.length === 0) {
      setError('这个相簿还没有内容。');
      return;
    }

    const slideshow = new Slideshow(stageRef.current, manifest.items, {
      intervalMs: seconds * 1000,
      muted: readStoredMuted(),
      onIndexChange: (index) => setPosition(index),
      onPausedChange: setPaused,
      onMutedChange: (isMuted, forced) => {
        setMuted(isMuted);
        if (forced) {
          // The browser refused unmuted playback; say so instead of letting the sound just vanish.
          localStorage.setItem(MUTED_STORAGE_KEY, '1');
          setNotice('浏览器不允许带声音自动播放，已自动静音。轻触喇叭按钮可开声音。');
        }
      },
      onItemFailed: (item) => setNotice(`有一项加载失败，已跳过（${item.contentHash.slice(0, 8)}）。`),
      onAllFailed: () => setError('这个相簿里所有内容都加载失败了。请检查网络。'),
    });
    slideshowRef.current = slideshow;
    // ?i=N comes from the list page, so tapping a thumbnail opens that exact item.
    const requested = Number(new URLSearchParams(window.location.search).get('i'));
    const startAt = Number.isInteger(requested) && requested >= 0 && requested < manifest.items.length ? requested : 0;
    void slideshow.start(startAt);
    revealChrome();

    const releaseWakeLock = keepScreenAwake();

    return () => {
      releaseWakeLock();
      slideshow.destroy();
      slideshowRef.current = null;
    };
    // `seconds` is applied through setIntervalMs below; rebuilding on every change would restart the
    // album from the first slide.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manifest]);

  useEffect(() => {
    slideshowRef.current?.setIntervalMs(seconds * 1000);
    localStorage.setItem(INTERVAL_STORAGE_KEY, String(seconds));
  }, [seconds]);

  /**
   * Precaching starts automatically on entering an album. This is the whole cost model: once every
   * object is local, looping the album for hours transfers nothing. Playback begins immediately and
   * runs alongside the caching, so a slow link (the parents' connection from China) only affects how
   * long the "caching" pill stays up, never whether the frame works.
   */
  useEffect(() => {
    if (!manifest) return;

    if (!serviceWorkerSupported()) {
      setCacheState({ phase: 'unsupported' });
      return;
    }

    let cancelled = false;

    const unsubscribe = onPrecacheEvents((event) => {
      if (cancelled || event.data.albumId !== manifest.album.id) return;
      if (event.type === 'progress') {
        setCacheState({ phase: 'caching', done: event.data.done, total: event.data.total });
      } else if (event.data.quotaExceeded) {
        setCacheState({ phase: 'partial', done: event.data.done, total: event.data.total, reason: 'quota' });
      } else if (event.data.failed > 0) {
        setCacheState({ phase: 'partial', done: event.data.done, total: event.data.total, reason: 'failed' });
      } else if (!event.data.cancelled) {
        setCacheState({ phase: 'complete' });
      }
    });

    void (async () => {
      try {
        const status = await albumCacheStatus(manifest.items);
        if (cancelled) return;
        if (status.cached >= status.total) {
          setCacheState({ phase: 'complete' });
          return;
        }
        setCacheState({ phase: 'caching', done: status.cached, total: status.total });
        await startPrecache(manifest.album.id, manifest.items);
      } catch {
        if (!cancelled) setCacheState({ phase: 'unsupported' });
      }
    })();

    return () => {
      cancelled = true;
      unsubscribe();
      // The job is deliberately left running: finishing the cache in the background is the point.
    };
  }, [manifest]);

  useEffect(() => {
    const onChange = () => setFullscreen(isFullscreen());
    document.addEventListener('fullscreenchange', onChange);
    document.addEventListener('webkitfullscreenchange', onChange);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      document.removeEventListener('webkitfullscreenchange', onChange);
    };
  }, []);

  useEffect(() => {
    if (!notice) return;
    const timer = setTimeout(() => setNotice(null), 4000);
    return () => clearTimeout(timer);
  }, [notice]);

  useEffect(() => () => {
    if (hideTimer.current !== null) clearTimeout(hideTimer.current);
  }, []);

  // Keyboard control, for the desktop/TV case.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const slideshow = slideshowRef.current;
      if (!slideshow) return;
      if (event.key === 'ArrowRight') slideshow.next();
      else if (event.key === 'ArrowLeft') slideshow.prev();
      else if (event.key === ' ') {
        event.preventDefault();
        slideshow.setPaused(!slideshow.paused);
      } else if (event.key === 'Escape') navigate('/');
      else return;
      revealChrome();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [revealChrome]);

  if (error) {
    return (
      <div class="center-note">
        <span class="note-icon">
          <Icon name="alert" size={40} />
        </span>
        <h1>无法播放</h1>
        <p>{error}</p>
        <p>
          <button class="btn-icon-label" onClick={() => navigate('/')}>
            <Icon name="chevronLeft" size={17} />
            返回相簿列表
          </button>
        </p>
      </div>
    );
  }

  const total = manifest?.items.length ?? 0;
  const currentItem = manifest?.items[position];
  const chromeClass = chromeVisible ? '' : ' chrome-hidden';

  return (
    <div class="stage-page" onClick={revealChrome} onTouchStart={revealChrome} onMouseMove={revealChrome}>
      <div class="stage" ref={stageRef} />

      {!manifest && (
        <div class="stage-message">
          <span class="spinner" />
          正在加载…
        </div>
      )}
      {notice && <div class="stage-message">{notice}</div>}

      <div class={'topbar' + chromeClass}>
        <button
          class="with-label"
          onClick={(e) => {
            e.stopPropagation();
            navigate('/');
          }}
          aria-label="返回相簿列表"
        >
          <Icon name="chevronLeft" size={18} />
          相簿
        </button>
        <span class="album-name">{manifest?.album.title ?? ''}</span>
        <button
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/a/${encodeURIComponent(slug)}/list`);
          }}
          aria-label="文件列表"
          title="文件列表（可下载单张）"
        >
          <Icon name="list" size={20} />
        </button>
        <span class="position">{total ? `${position + 1}/${total}` : ''}</span>
      </div>

      <CachePill state={cacheState} visible={chromeVisible} />

      <div class={'controls' + chromeClass}>
        <div class="control-row">
          <button
            onClick={(e) => {
              e.stopPropagation();
              slideshowRef.current?.prev();
            }}
            aria-label="上一张"
          >
            <Icon name="prev" size={20} />
          </button>
          <button
            class="play-toggle"
            onClick={(e) => {
              e.stopPropagation();
              const slideshow = slideshowRef.current;
              if (slideshow) slideshow.setPaused(!slideshow.paused);
            }}
            aria-label={paused ? '继续播放' : '定格在这一张'}
            title={paused ? '继续播放' : '定格在这一张'}
          >
            <Icon name={paused ? 'play' : 'pause'} size={24} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              slideshowRef.current?.next();
            }}
            aria-label="下一张"
          >
            <Icon name="next" size={20} />
          </button>
          <span />
          {/* Only shown when there is something to hear — a speaker button on a photos-only album
              would just be a dead control. */}
          {manifest?.items.some((item) => item.kind === 'video') && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                const slideshow = slideshowRef.current;
                if (!slideshow) return;
                const next = !slideshow.muted;
                slideshow.setMuted(next);
                setMuted(next);
                localStorage.setItem(MUTED_STORAGE_KEY, next ? '1' : '0');
              }}
              aria-label={muted ? '开启声音' : '静音'}
              title={muted ? '开启声音' : '静音'}
            >
              <Icon name={muted ? 'volumeOff' : 'volumeOn'} size={20} />
            </button>
          )}
          <select
            value={String(seconds)}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => setSeconds(Number((e.target as HTMLSelectElement).value))}
            aria-label="每张停留时间"
          >
            {INTERVAL_CHOICES.map((choice) => (
              <option key={choice} value={String(choice)}>
                {choice} 秒
              </option>
            ))}
          </select>
          {fullscreenSupported() && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                void toggleFullscreen();
              }}
              aria-label={fullscreen ? '退出全屏' : '全屏'}
              title={fullscreen ? '退出全屏' : '全屏'}
            >
              <Icon name={fullscreen ? 'collapse' : 'expand'} size={20} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// --- shell ------------------------------------------------------------------

function App() {
  const path = useRoute();
  const [session, setSession] = useState<SessionInfo | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);

  // Registered before anything else needs it, and failure is non-fatal: without a worker the frame
  // still plays, it just pays for bandwidth on every loop.
  useEffect(() => {
    void registerServiceWorker();
  }, []);

  useEffect(() => {
    getSession()
      .then(setSession)
      .catch((err) => {
        if (isUnauthenticated(err)) {
          window.location.href = '/join';
          return;
        }
        setAuthError(err instanceof ApiError ? err.message : String(err));
      });
  }, []);

  useEffect(() => {
    if (!session) return;
    return startHeartbeat(() => {
      // Revoked: drop everything cached locally before bouncing, so the frame cannot keep showing
      // photos it has already downloaded.
      void clearLocalMedia().finally(() => {
        window.location.href = '/join';
      });
    });
  }, [session]);

  if (authError) {
    return (
      <div class="center-note">
        <span class="note-icon">
          <Icon name="alert" size={40} />
        </span>
        <h1>连接不上</h1>
        <p>{authError}</p>
        <p>
          <button class="btn-icon-label primary" onClick={() => window.location.reload()}>
            <Icon name="refresh" size={17} />
            重试
          </button>
        </p>
      </div>
    );
  }

  if (!session) {
    return (
      <div class="center-note">
        <p>
          <span class="spinner" />
          正在检查授权…
        </p>
      </div>
    );
  }

  const listMatch = /^\/a\/([^/]+)\/list\/?$/.exec(path);
  if (listMatch) {
    const slug = decodeURIComponent(listMatch[1]!);
    return (
      <AlbumList
        slug={slug}
        onPlay={(index) => navigate(`/a/${encodeURIComponent(slug)}?i=${index}`)}
        onBack={() => navigate('/')}
      />
    );
  }

  const albumMatch = /^\/a\/([^/]+)\/?$/.exec(path);
  if (albumMatch) return <SlideshowView slug={decodeURIComponent(albumMatch[1]!)} />;
  return <AlbumGrid session={session} />;
}

/** Best-effort wipe of locally cached media. Used when the device turns out to be revoked. */
async function clearLocalMedia(): Promise<void> {
  try {
    if (typeof caches === 'undefined') return;
    const names = await caches.keys();
    await Promise.all(names.map((name) => caches.delete(name)));
  } catch {
    // Nothing useful to do if the browser refuses.
  }
}

render(<App />, document.getElementById('app')!);
